import { createContext } from 'react'

/**
 * Enum of the different types of Procurated users.
 *
 * - `UNAUTHED` - A user who is not logged in.
 * - `SUPPLIER_USER` - A user who belongs to a supplier organization.
 * - `AGENCY_USER` - A government user who belongs to an agency organization.
 *
 * `as const` is necessary to ensure proper typing.
 */
export const USER_TYPES = {
  UNAUTHED: 'unauthed',
  SUPPLIER_USER: 'supplier',
  AGENCY_USER: 'agency'
} as const

/**
 * Enum of the different states a user's account can be in.
 *
 * - `VERIFIED` - The user has been verified by admins and has full access to Procurated.
 * - `UNVERIFIED` - The user has not been fully verified and has limited access to Procurated services.
 *
 * `as const` is necessary to ensure proper typing.
 */
export const USER_STATES = {
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified'
} as const

/**
 * Takes one of the object enum types above and gets their value type.
 * `keyof (typeof USER_TYPES)` is verbose.
 */
type ObjectEnum<T extends { [key: string]: unknown }> = T[keyof T]

export type UserType = ObjectEnum<typeof USER_TYPES>
export type UserState = ObjectEnum<typeof USER_STATES>

// Begin Canary Access Tier Types
/**
 * Enum of the different access tiers available to supplier users for supplier canary.
 *
 * - `Basic` - Basic access tier with limited features.
 * - `Plus` - Enhanced access tier with additional features.
 * - `Complete` - Full access tier with all available features.
 *
 * Order matters for tier comparison logic.
 * `as const` is necessary to ensure proper typing.
 */
export const SupplierAccessTier = {
  // Order matters
  Basic: 'basic',
  Plus: 'plus',
  Complete: 'complete'
} as const

/**
 * Array of supplier access tier values in order for tier comparison.
 */
const TierOrder = Object.values(SupplierAccessTier)

/**
 * Type representing one of the supplier access tier options for supplier canary.
 */
export type SupplierAccessTierOption = (typeof SupplierAccessTier)[keyof typeof SupplierAccessTier]

/**
 * Type representing one of the agency access tier options - null for now because agency users don't have access tiers for agency canary.
 */
type AgencyAccessTierOption = null

/**
 * Type representing a user's possible access tier option.
 */
export type userAccessTierOption = SupplierAccessTierOption | AgencyAccessTierOption
// End Canary Access Tier Types

/**
 * The authentication state of the current user.
 *
 * Includes information about the user as well as their authentication status.
 */
export interface AuthState {
  canReplyToReview: boolean
  userType: UserType
  userState: UserState
  isOwner: boolean
  isTxVpts: boolean
  userIsGooglebot: boolean
  canaryUserSupplierTier: userAccessTierOption
}

export const agencyUser = (authContext: AuthState): boolean => {
  return authContext.userType === USER_TYPES.AGENCY_USER
}

/**
 *
 * @param authContext
 * @returns If the user is allowed to report abuse on review
 */
export const canReportAbuse = (authContext: AuthState): boolean => {
  return !authContext.isOwner
}

/**
 * @param authContext The current auth state.
 * @returns Whether the user is a public user (either unauthenticated or unverified).
 */
export const showPublicOnly = (authContext: AuthState): boolean => {
  return authContext?.userType === USER_TYPES.UNAUTHED || authContext?.userState === USER_STATES.UNVERIFIED
}

/**
 * @param authContext The current auth state.
 * @returns Whether the user is an agency user and is verified.
 */
export const verifiedAgencyUser = (authContext: AuthState): boolean => {
  return authContext?.userType === USER_TYPES.AGENCY_USER && authContext.userState === USER_STATES.VERIFIED
}

/**
 * @param user The user to check the permissions of.
 * @param supplier The supplier with whether the user can edit them.
 * @returns Whether the user can edit the supplier as a supplier user.
 */
export const canEditSupplier = (user: { userType: UserType }, supplier: { canEdit?: boolean }): boolean => {
  return supplier.canEdit === true && user.userType === USER_TYPES.SUPPLIER_USER
}

/**
 * @param user The user to check the permissions of.
 * @param supplier The supplier to check the user's permissions for.
 * @returns Whether the user can reply to a review on the supplier or not.
 */
export const canReplyToReview = (user: { userType: UserType, canaryUserSupplierTier?: userAccessTierOption }, supplier: { pro?: boolean, canEdit?: boolean }): boolean => {
  const canaryUserSupplierTier = user.canaryUserSupplierTier ?? null
  const legacyAuthCheck = canEditSupplier(user, supplier) && supplier.pro === true
  const canaryAuthCheck = userHasAccessTier(canaryUserSupplierTier, SupplierAccessTier.Plus)

  return legacyAuthCheck || canaryAuthCheck
}

/**
 * @param authContext The auth context.
 * @returns Whether the user is an owner of the supplier.
 */
export const isSupplierOwner = (authContext: AuthState): boolean => {
  return (authContext.userType === USER_TYPES.SUPPLIER_USER) && authContext.isOwner
}

/**
 * @param currentUserTier The access tier of the current user.
 * @param componentTier The minimum access tier required.
 * @returns Whether the user meets or exceeds the required access tier.
 */
export const userHasAccessTier = (currentUserTier: userAccessTierOption, componentTier: SupplierAccessTierOption): boolean => {
  if (currentUserTier === null) return false
  return TierOrder.indexOf(currentUserTier) >= TierOrder.indexOf(componentTier)
}

/**
 * Default context.
 */
export const DEFAULT_AUTH_CONTEXT = {
  canReplyToReview: false,
  isOwner: false,
  isTxVpts: false,
  userState: USER_STATES.UNVERIFIED,
  userType: USER_TYPES.UNAUTHED,
  userIsGooglebot: false,
  canaryUserSupplierTier: null
}

/**
 * Context containing the current authentication state.
 */
export const AuthContext = createContext<AuthState>(DEFAULT_AUTH_CONTEXT)
